import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/axiosConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Loader2, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';

function effectiveCatalogIdx(idx, s) {
  if (idx < 0) return new Set();
  const tid = s.offeredSorted[idx];
  if (idx === 0) return new Set(s.tierCatalogSelections[tid] || []);
  if (s.inheritFromPrevious[tid]) {
    return new Set([...effectiveCatalogIdx(idx - 1, s), ...(s.additionalCatalog[tid] || [])]);
  }
  return new Set(s.tierCatalogSelections[tid] || []);
}

function effectiveCustomIdx(idx, s) {
  if (idx < 0) return new Set();
  const tid = s.offeredSorted[idx];
  if (idx === 0) return new Set(s.tierCustomSelections[tid] || []);
  if (s.inheritFromPrevious[tid]) {
    return new Set([...effectiveCustomIdx(idx - 1, s), ...(s.additionalCustom[tid] || [])]);
  }
  return new Set(s.tierCustomSelections[tid] || []);
}

function buildSnap(
  offeredSorted,
  tierCatalogSelections,
  tierCustomSelections,
  inheritFromPrevious,
  additionalCatalog,
  additionalCustom
) {
  return {
    offeredSorted,
    tierCatalogSelections,
    tierCustomSelections,
    inheritFromPrevious,
    additionalCatalog,
    additionalCustom,
  };
}

/**
 * Manage offered coaching tiers, standard catalog services per tier, and coach-only custom services.
 * Supports "Same as [prior plan] +" to inherit services and only edit add-ons.
 */
export default function CoachOfferingsEditor({ className, disabled: parentDisabled }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [allTiers, setAllTiers] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [offeredTierIds, setOfferedTierIds] = useState([]);
  const [customServices, setCustomServices] = useState([]);
  const [tierCatalogSelections, setTierCatalogSelections] = useState({});
  const [tierCustomSelections, setTierCustomSelections] = useState({});
  const [inheritFromPrevious, setInheritFromPrevious] = useState({});
  const [additionalCatalog, setAdditionalCatalog] = useState({});
  const [additionalCustom, setAdditionalCustom] = useState({});
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const [tiersRes, catRes, offRes] = await Promise.all([
        api.get('/api/coaches/tiers'),
        api.get('/api/coaches/service-catalog'),
        api.get('/api/coaches/me/offerings'),
      ]);
      setAllTiers(tiersRes.data?.tiers || []);
      setCatalogServices(catRes.data?.services || []);
      const o = offRes.data;
      const offered = [...(o.offeredTierIds || [])];
      const tierMeta = {};
      (tiersRes.data?.tiers || []).forEach((row) => {
        tierMeta[row.id] = row;
      });
      offered.sort((a, b) => {
        const oa = tierMeta[a]?.display_order ?? tierMeta[a]?.displayOrder;
        const ob = tierMeta[b]?.display_order ?? tierMeta[b]?.displayOrder;
        if (oa != null && ob != null && oa !== ob) return oa - ob;
        return a - b;
      });

      const tc = {};
      const tu = {};
      for (const row of o.tierServices || []) {
        tc[row.tierId] = new Set(row.catalogServiceIds || []);
        tu[row.tierId] = new Set(row.customServiceIds || []);
      }

      const inferInherit = {};
      const inferAddCat = {};
      const inferAddCust = {};
      for (let i = 1; i < offered.length; i += 1) {
        const tid = offered[i];
        const prevTid = offered[i - 1];
        const prevC = new Set(tc[prevTid] || []);
        const currC = new Set(tc[tid] || []);
        const prevU = new Set(tu[prevTid] || []);
        const currU = new Set(tu[tid] || []);
        if (prevC.size === 0 && prevU.size === 0) {
          inferInherit[tid] = false;
          continue;
        }
        const catSub = [...prevC].every((x) => currC.has(x));
        const custSub = [...prevU].every((x) => currU.has(x));
        if (catSub && custSub) {
          inferInherit[tid] = true;
          inferAddCat[tid] = new Set([...currC].filter((x) => !prevC.has(x)));
          inferAddCust[tid] = new Set([...currU].filter((x) => !prevU.has(x)));
        } else {
          inferInherit[tid] = false;
        }
      }

      setOfferedTierIds(offered);
      setCustomServices(o.customServices || []);
      setTierCatalogSelections(tc);
      setTierCustomSelections(tu);
      setInheritFromPrevious(inferInherit);
      setAdditionalCatalog(inferAddCat);
      setAdditionalCustom(inferAddCust);
    } catch {
      setMsg({ type: 'err', text: t('coaches.offeringsLoadError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const tierMeta = useMemo(() => {
    const m = {};
    for (const row of allTiers) {
      m[row.id] = row;
    }
    return m;
  }, [allTiers]);

  const offeredSorted = useMemo(() => {
    return [...offeredTierIds].sort((a, b) => {
      const oa = tierMeta[a]?.display_order ?? tierMeta[a]?.displayOrder;
      const ob = tierMeta[b]?.display_order ?? tierMeta[b]?.displayOrder;
      if (oa != null && ob != null && oa !== ob) return oa - ob;
      return a - b;
    });
  }, [offeredTierIds, tierMeta]);

  const toggleOfferedTier = (tierId) => {
    const id = parseInt(tierId, 10);
    setOfferedTierIds((prev) => {
      if (prev.includes(id)) {
        setTierCatalogSelections((s) => {
          const n = { ...s };
          delete n[id];
          return n;
        });
        setTierCustomSelections((s) => {
          const n = { ...s };
          delete n[id];
          return n;
        });
        setInheritFromPrevious((s) => {
          const n = { ...s };
          delete n[id];
          return n;
        });
        setAdditionalCatalog((s) => {
          const n = { ...s };
          delete n[id];
          return n;
        });
        setAdditionalCustom((s) => {
          const n = { ...s };
          delete n[id];
          return n;
        });
        return prev.filter((x) => x !== id);
      }
      return [...prev, id].sort((a, b) => a - b);
    });
  };

  const toggleCatalog = (tierId, catalogId, prevEffCatSet) => {
    const tid = parseInt(tierId, 10);
    const cid = parseInt(catalogId, 10);
    if (inheritFromPrevious[tid] && prevEffCatSet?.has(cid)) return;
    if (inheritFromPrevious[tid]) {
      setAdditionalCatalog((prev) => {
        const set = new Set(prev[tid] || []);
        if (set.has(cid)) set.delete(cid);
        else set.add(cid);
        return { ...prev, [tid]: set };
      });
      return;
    }
    setTierCatalogSelections((prev) => {
      const set = new Set(prev[tid] || []);
      if (set.has(cid)) set.delete(cid);
      else set.add(cid);
      return { ...prev, [tid]: set };
    });
  };

  const toggleCustom = (tierId, customId, prevEffCustSet) => {
    const tid = parseInt(tierId, 10);
    const uid = parseInt(customId, 10);
    if (inheritFromPrevious[tid] && prevEffCustSet?.has(uid)) return;
    if (inheritFromPrevious[tid]) {
      setAdditionalCustom((prev) => {
        const set = new Set(prev[tid] || []);
        if (set.has(uid)) set.delete(uid);
        else set.add(uid);
        return { ...prev, [tid]: set };
      });
      return;
    }
    setTierCustomSelections((prev) => {
      const set = new Set(prev[tid] || []);
      if (set.has(uid)) set.delete(uid);
      else set.add(uid);
      return { ...prev, [tid]: set };
    });
  };

  const setInheritForTier = (tid, enabled) => {
    const idx = offeredSorted.indexOf(tid);
    if (idx <= 0) return;

    if (enabled) {
      const partialSnap = buildSnap(
        offeredSorted,
        tierCatalogSelections,
        tierCustomSelections,
        { ...inheritFromPrevious, [tid]: false },
        additionalCatalog,
        additionalCustom
      );
      const prevCat = effectiveCatalogIdx(idx - 1, partialSnap);
      const prevCust = effectiveCustomIdx(idx - 1, partialSnap);
      const currCat = new Set(tierCatalogSelections[tid] || []);
      const currCust = new Set(tierCustomSelections[tid] || []);
      setAdditionalCatalog((ac) => ({
        ...ac,
        [tid]: new Set([...currCat].filter((x) => !prevCat.has(x))),
      }));
      setAdditionalCustom((au) => ({
        ...au,
        [tid]: new Set([...currCust].filter((x) => !prevCust.has(x))),
      }));
      setInheritFromPrevious((p) => ({ ...p, [tid]: true }));
      return;
    }

    const snapTrue = buildSnap(
      offeredSorted,
      tierCatalogSelections,
      tierCustomSelections,
      { ...inheritFromPrevious, [tid]: true },
      additionalCatalog,
      additionalCustom
    );
    const mergedC = effectiveCatalogIdx(idx, snapTrue);
    const mergedU = effectiveCustomIdx(idx, snapTrue);
    setTierCatalogSelections((s) => ({ ...s, [tid]: mergedC }));
    setTierCustomSelections((s) => ({ ...s, [tid]: mergedU }));
    setInheritFromPrevious((p) => ({ ...p, [tid]: false }));
  };

  const saveOfferings = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const sorted = [...offeredSorted];
      const snap = buildSnap(
        sorted,
        tierCatalogSelections,
        tierCustomSelections,
        inheritFromPrevious,
        additionalCatalog,
        additionalCustom
      );
      const tierServices = sorted.map((tid, idx) => ({
        tierId: tid,
        catalogServiceIds: [...effectiveCatalogIdx(idx, snap)],
        customServiceIds: [...effectiveCustomIdx(idx, snap)],
      }));
      await api.put('/api/coaches/me/offerings', { offeredTierIds: sorted, tierServices });
      setMsg({ type: 'ok', text: t('coaches.offeringsSaved') });
    } catch (e) {
      setMsg({
        type: 'err',
        text: e.response?.data?.error || t('coaches.offeringsError'),
      });
    } finally {
      setSaving(false);
    }
  };

  const addCustom = async () => {
    const title = newTitle.trim();
    if (!title) return;
    try {
      const res = await api.post('/api/coaches/me/custom-services', {
        title,
        description: newDesc.trim() || null,
      });
      setCustomServices((prev) => [...prev, res.data]);
      setNewTitle('');
      setNewDesc('');
    } catch {
      setMsg({ type: 'err', text: t('coaches.offeringsError') });
    }
  };

  const removeCustom = async (id) => {
    try {
      await api.delete(`/api/coaches/me/custom-services/${id}`);
      setCustomServices((prev) => prev.filter((c) => c.id !== id));
      setTierCustomSelections((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          const s = new Set(next[k]);
          s.delete(id);
          next[k] = s;
        }
        return next;
      });
      setAdditionalCustom((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          const s = new Set(next[k]);
          s.delete(id);
          next[k] = s;
        }
        return next;
      });
    } catch {
      setMsg({ type: 'err', text: t('coaches.offeringsError') });
    }
  };

  const renderTierEditor = (tid, tierIndex) => {
    const prevName =
      tierIndex > 0 ? tierMeta[offeredSorted[tierIndex - 1]]?.name || `Plan ${offeredSorted[tierIndex - 1]}` : '';
    const inherits = !!inheritFromPrevious[tid];
    const snap = buildSnap(
      offeredSorted,
      tierCatalogSelections,
      tierCustomSelections,
      inheritFromPrevious,
      additionalCatalog,
      additionalCustom
    );
    const prevEffCat = tierIndex > 0 ? effectiveCatalogIdx(tierIndex - 1, snap) : new Set();
    const prevEffCust = tierIndex > 0 ? effectiveCustomIdx(tierIndex - 1, snap) : new Set();
    const inheritedCount = prevEffCat.size + prevEffCust.size;

    const catalogList = catalogServices;
    const customList = customServices;

    return (
      <Card key={tid} className='border-border overflow-hidden'>
        <CardHeader className='py-3 px-4 bg-muted/30'>
          <CardTitle className='text-sm'>{tierMeta[tid]?.name || `Plan ${tid}`}</CardTitle>
        </CardHeader>
        <CardContent className='pt-4 px-4 pb-4 space-y-4'>
          {tierIndex > 0 && (
            <div className='flex flex-col gap-2 rounded-lg border border-border bg-card p-3'>
              <div className='flex items-center justify-between gap-3'>
                <div className='space-y-0.5 min-w-0'>
                  <Label htmlFor={`inherit-${tid}`} className='text-sm font-medium cursor-pointer'>
                    {t('coaches.sameAsPriorPlan', { planName: prevName })}
                  </Label>
                  <p className='text-xs text-muted-foreground'>{t('coaches.sameAsPriorPlanHint')}</p>
                </div>
                <Switch
                  id={`inherit-${tid}`}
                  checked={inherits}
                  onCheckedChange={(v) => setInheritForTier(tid, v)}
                  disabled={parentDisabled}
                />
              </div>
              {inherits && inheritedCount > 0 && (
                <p className='text-xs text-muted-foreground rounded-md bg-muted/50 px-2 py-1.5 border border-border/60'>
                  {t('coaches.inheritedFromPlan', { planName: prevName, count: inheritedCount })}
                </p>
              )}
            </div>
          )}

          <div>
            <p className='text-xs font-medium text-muted-foreground mb-2'>
              {inherits && tierIndex > 0
                ? t('coaches.additionalServicesHeading', { planName: prevName })
                : t('coaches.servicesSectionTitle')}
            </p>
            <div className='space-y-2 max-h-56 overflow-y-auto pr-1'>
              {catalogList.map((svc) => {
                const key = svc.serviceKey;
                const inheritedFromPrior = inherits && prevEffCat.has(svc.id);
                const checked = inherits
                  ? inheritedFromPrior || !!additionalCatalog[tid]?.has(svc.id)
                  : !!tierCatalogSelections[tid]?.has(svc.id);
                const rowDisabled = parentDisabled || inheritedFromPrior;
                return (
                  <label
                    key={svc.id}
                    className={cn(
                      'flex items-start gap-2 rounded-md p-1 -m-1',
                      rowDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-accent/50'
                    )}
                  >
                    <input
                      type='checkbox'
                      checked={checked}
                      disabled={rowDisabled}
                      onChange={() => toggleCatalog(tid, svc.id, prevEffCat)}
                      className='mt-1 h-4 w-4 shrink-0 rounded border-input text-primary disabled:cursor-not-allowed'
                    />
                    <span className='text-sm min-w-0'>
                      <span className='text-foreground font-medium block'>
                        {t(`coaches.serviceCatalog.${key}.name`)}
                      </span>
                      <span className='block text-xs text-muted-foreground mt-0.5'>
                        {t(`coaches.serviceCatalog.${key}.description`)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {customList.length > 0 && (
            <div className='space-y-2 pt-2 border-t border-border'>
              <p className='text-xs text-muted-foreground'>{t('coaches.addCustomService')}</p>
              {customList.map((c) => {
                const inheritedFromPrior = inherits && prevEffCust.has(c.id);
                const checked = inherits
                  ? inheritedFromPrior || !!additionalCustom[tid]?.has(c.id)
                  : !!tierCustomSelections[tid]?.has(c.id);
                const rowDisabled = parentDisabled || inheritedFromPrior;
                return (
                  <label
                    key={c.id}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-1 -mx-1 py-0.5',
                      rowDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-accent/50'
                    )}
                  >
                    <input
                      type='checkbox'
                      checked={checked}
                      disabled={rowDisabled}
                      onChange={() => toggleCustom(tid, c.id, prevEffCust)}
                      className='h-4 w-4 shrink-0 rounded border-input text-primary disabled:cursor-not-allowed'
                    />
                    <span className='text-sm text-foreground truncate'>{c.title}</span>
                  </label>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className='flex justify-center py-6'>
        <Loader2 className='h-7 w-7 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className='space-y-2'>
        <Label>{t('coaches.offeredTiersLabel')}</Label>
        <div className='flex flex-col gap-2 rounded-lg border border-border p-3'>
          {allTiers.map((tier) => (
            <label key={tier.id} className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                checked={offeredTierIds.includes(tier.id)}
                onChange={() => toggleOfferedTier(tier.id)}
                className='h-4 w-4 rounded border-input text-primary'
              />
              <span className='text-sm text-foreground'>{tier.name}</span>
            </label>
          ))}
        </div>
      </div>

      {offeredSorted.length === 0 ? (
        <p className='text-xs text-muted-foreground'>{t('coaches.selectTiersFirst')}</p>
      ) : (
        <>
          <p className='text-sm font-medium text-foreground'>{t('coaches.plansAndServicesTitle')}</p>
          <div className='space-y-3'>
            {offeredSorted.map((tid, tierIndex) => renderTierEditor(tid, tierIndex))}
          </div>
        </>
      )}

      <div className='space-y-2 rounded-lg border border-border p-3'>
        <p className='text-sm font-medium text-foreground'>{t('coaches.addCustomService')}</p>
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder={t('coaches.customServiceTitleLabel')}
        />
        <Textarea
          rows={2}
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value)}
          placeholder={t('coaches.customServiceDescLabel')}
          className='resize-y min-h-[60px]'
        />
        <Button type='button' variant='secondary' size='sm' onClick={addCustom} disabled={!newTitle.trim()}>
          {t('coaches.addCustomService')}
        </Button>
      </div>

      {customServices.length > 0 && (
        <ul className='space-y-1 text-sm'>
          {customServices.map((c) => (
            <li
              key={c.id}
              className='flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5'
            >
              <span className='truncate text-foreground'>{c.title}</span>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='shrink-0 h-8 w-8 text-destructive hover:text-destructive'
                onClick={() => removeCustom(c.id)}
                aria-label={t('coaches.removeCustomService')}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {msg && (
        <p
          className={cn('text-xs', msg.type === 'ok' ? 'text-green-600 dark:text-green-500' : 'text-destructive')}
          role='status'
        >
          {msg.text}
        </p>
      )}

      <Button
        type='button'
        onClick={saveOfferings}
        disabled={saving || parentDisabled || offeredSorted.length === 0}
      >
        {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('coaches.saveOfferings')}
      </Button>
    </div>
  );
}
